import React, { useEffect, useState } from "react";
import { Alert, Button, CircularProgress, TextField, Typography } from "@mui/material";
import { toast } from "react-toastify";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import Logo from "../assets/parallex-logo.png";

const API_BASE = "https://parallexsms.approot.ng";

const OTP = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [userId, setUserId] = useState(location.state?.userId ?? null);
  const [email] = useState(location.state?.email ?? "");
  // Kept only in in-memory React Router navigation state to preserve the existing
  // login.php-based resend flow; never write the password to localStorage/sessionStorage.
  const [credentials] = useState(() => ({
    email: location.state?.email ?? "",
    password: location.state?.password ?? "",
  }));
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!location.state?.userId) navigate("/login", { replace: true });
  }, [location.state, navigate]);

  const handleVerify = async (event) => {
    event.preventDefault();
    if (!/^\d{6}$/.test(otp)) {
      setError("Enter the 6-digit verification code.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_BASE}/verify_otp.php`,
        { userId, otp },
        { headers: { "Content-Type": "application/json" } }
      );
      if (response.data.success) {
        localStorage.setItem("parallextoken", response.data.token);
        toast.success(response.data.message || "Login successful!");
        navigate("/dashboard", { replace: true });
      } else {
        setError(response.data.message || "Invalid or expired verification code.");
      }
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || "Could not verify the code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!credentials.email || !credentials.password) {
      toast.error("Your login session has expired. Please return to login and try again.");
      navigate("/login", { replace: true });
      return;
    }
    setResending(true);
    try {
      const response = await axios.post(
        `${API_BASE}/login.php`,
        credentials,
        { headers: { "Content-Type": "application/json" } }
      );
      if (response.data.success && response.data.requiresOTP) {
        setUserId(response.data.userId);
        setOtp("");
        setError("");
        toast.success(response.data.message || "A new verification code has been sent.");
      } else {
        toast.error(response.data.message || "Could not resend the verification code.");
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Could not resend the code. Please try again.");
    } finally {
      setResending(false);
    }
  };

  if (!location.state?.userId) return null;

  return (
    <main className="min-h-screen flex flex-col md:flex-row bg-[#f8f7fc]">
      <section className="relative flex min-h-[260px] w-full flex-col items-center justify-center overflow-hidden bg-[#34206f] px-6 py-12 text-white md:min-h-screen md:w-1/2 sm:px-10">
        <img
  src={Logo}
  alt="Parallex Bank"
  className="mb-8 h-16 w-auto max-w-[240px] object-contain drop-shadow-sm"
/>
        <Typography variant="h4" className="mb-4 text-center font-bold">
          Secure Verification
        </Typography>
        <Typography className="max-w-md text-center text-sm leading-7 text-white/85 sm:text-base">
          Enter the one-time code sent to your email to securely access your account.
        </Typography>
      </section>

      <section className="flex w-full items-center justify-center bg-[#f8f7fc] px-4 py-10 sm:px-8 md:w-1/2 md:py-12">
        <form onSubmit={handleVerify} className="w-full max-w-md rounded-3xl border border-[#eeeaf7] bg-white px-6 py-8 shadow-[0_20px_60px_rgba(43,25,90,0.10)] sm:px-10 sm:py-10">
          <div className="mb-8 text-center">
            <Typography variant="h5" className="font-bold text-primary">Enter Verification Code</Typography>
            <Typography variant="body2" className="mt-2 text-gray-600">
              We sent a 6-digit code to <span className="font-medium">{email}</span>
            </Typography>
          </div>

          {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

          <div className="space-y-5">
            <TextField
              autoFocus
              fullWidth
              label="6-digit OTP"
              value={otp}
              onChange={(event) => setOtp(event.target.value.replace(/\D/g, "").slice(0, 6))}
              inputProps={{ inputMode: "numeric", maxLength: 6, pattern: "[0-9]{6}" }}
              placeholder="000000"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', backgroundColor: '#fff', '&.Mui-focused fieldset': { borderColor: '#DB9E27' } }, '& .MuiInputLabel-root.Mui-focused': { color: '#DB9E27' } }}
            />
            <Button type="submit" variant="contained" fullWidth disabled={loading || resending} sx={{ backgroundColor: "#DB9E27", color: "#fff", fontWeight: "bold", py: 1.4, '&:hover': { backgroundColor: '#c5891e' } }}>
              {loading ? <><CircularProgress size={20} sx={{ color: "white", mr: 1 }} /> Verifying...</> : "Verify & Login"}
            </Button>
            <div className="flex flex-col items-center gap-1 pt-1">
              <Button type="button" onClick={handleResend} disabled={loading || resending} sx={{ color: "#DB9E27", textTransform: "none" }}>
                {resending ? "Resending..." : "Resend code"}
              </Button>
              <Button type="button" onClick={() => navigate("/login", { replace: true })} disabled={loading || resending} sx={{ color: "#6b7280", textTransform: "none" }}>
                Back to login
              </Button>
            </div>
          </div>
          <Typography variant="body2" className="mt-7 text-center text-gray-500">
           &copy; {new Date().getFullYear()} Parallex Bank. All rights reserved.
          </Typography>
        </form>
      </section>
    </main>
  );
};

export default OTP;
